"use client";

import { CinematicModal } from "@/components/ui/CinematicModal";
import { trackTutorEvent } from "@/lib/analytics/tutor-events";
import {
  cinematicModalInputClass,
  cinematicModalLabelClass,
  cinematicModalPrimaryButtonClass,
  cinematicModalSecondaryButtonClass,
  cinematicModalSubtitleClass,
  cinematicModalTitleClass,
} from "@/lib/cinematicModalStyles";
import { createLogger } from "@/lib/logger";
import { cn } from "@/lib/utils";
import { Check, GraduationCap, Loader2, Plus, Users } from "lucide-react";
import { usePostHog } from "posthog-js/react";
import { useCallback, useEffect, useState } from "react";

const logger = createLogger("ClassroomModal");

interface EnrolledClass {
  class_id: string;
  class_name: string;
  language_target: string;
  enrolled_at: string;
}

interface ClassroomModalProps {
  isOpen: boolean;
  onCloseAction: () => void;
  enrolledClasses: EnrolledClass[];
  selectedClassId: string | null;
  onClassSelectAction: (classId: string | null) => void;
  onJoinClassAction: (
    classCode: string
  ) => Promise<{ success: boolean; class?: any; alreadyEnrolled?: boolean }>;
  isLoading?: boolean;
  error?: string | null;
}

const tabButtonClass = (active: boolean) =>
  cn(
    "flex-1 px-4 py-2.5 text-sm font-semibold transition-colors",
    active
      ? "border-b-2 border-orange-400/70 bg-orange-500/10 text-orange-200"
      : "text-white/55 hover:bg-white/[0.04] hover:text-white"
  );

const classCardClass = (active: boolean) =>
  cn(
    "w-full rounded-lg border p-4 text-left transition-colors",
    active
      ? "border-orange-400/60 bg-orange-500/15 text-orange-100"
      : "border-white/10 bg-white/[0.04] text-white/80 hover:border-orange-300/35 hover:bg-white/[0.07]"
  );

export function ClassroomModal({
  isOpen,
  onCloseAction,
  enrolledClasses,
  selectedClassId,
  onClassSelectAction,
  onJoinClassAction,
  isLoading = false,
}: ClassroomModalProps) {
  const posthog = usePostHog();
  const [classCode, setClassCode] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [activeTab, setActiveTab] = useState<"my-classes" | "join">("my-classes");

  useEffect(() => {
    if (!isOpen) {
      setClassCode("");
      setJoinError(null);
      setSuccess(false);
      setActiveTab("my-classes");
    }
  }, [isOpen]);

  const handleJoinSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setJoinError(null);
    setSuccess(false);

    const normalized = classCode.trim().toUpperCase();
    if (!normalized || normalized.length < 4) {
      setJoinError("Please enter a valid class code");
      return;
    }

    try {
      setIsSubmitting(true);
      const result = await onJoinClassAction(normalized);
      if (!result.success || !result.class) {
        setJoinError("Unable to join class. Please check the code and try again.");
        return;
      }
      setSuccess(true);
      logger.info("Joined class successfully", { classId: result.class.id, normalized });
      trackTutorEvent(posthog, "student_enrolled", {
        source: "classroom_modal",
        surface: "fullscreen",
        class_id: result.class.id,
        language: result.class.language_target,
        already_enrolled: Boolean(result.alreadyEnrolled),
      });
      onClassSelectAction(result.class.id);

      setTimeout(() => {
        onCloseAction();
        setSuccess(false);
        setClassCode("");
        setActiveTab("my-classes");
      }, 1500);
    } catch (err) {
      logger.error("Error joining class", { error: err });
      setJoinError("An unexpected error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClassSelect = useCallback(
    (classId: string | null) => {
      onClassSelectAction(classId);
      onCloseAction();
    },
    [onClassSelectAction, onCloseAction]
  );

  return (
    <CinematicModal
      isOpen={isOpen}
      onClose={onCloseAction}
      maxWidth="lg"
      titleId="classroom-dialog-title"
      contentClassName="flex max-h-[85vh] flex-col p-0"
      panelClassName="flex max-h-[90vh] flex-col"
    >
      <div className="border-b border-white/10 bg-white/[0.03] px-5 py-4 sm:px-6">
        <div className="flex items-start gap-3 pr-10">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-orange-400/25 bg-orange-500/10 text-orange-200">
            <GraduationCap size={20} aria-hidden="true" />
          </div>
          <div>
            <h2 id="classroom-dialog-title" className={cinematicModalTitleClass}>
              Classroom
              <span className="ml-2 rounded-md bg-orange-400 px-1.5 py-0.5 text-[10px] font-extrabold text-black">
                BETA
              </span>
            </h2>
            <p className={cinematicModalSubtitleClass}>Manage your class enrollments</p>
          </div>
        </div>
      </div>

      <div className="flex border-b border-white/10">
        <button
          type="button"
          onClick={() => setActiveTab("my-classes")}
          className={tabButtonClass(activeTab === "my-classes")}
        >
          <span className="flex items-center justify-center gap-2">
            <Users size={16} aria-hidden="true" />
            My classes
          </span>
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("join")}
          className={tabButtonClass(activeTab === "join")}
        >
          <span className="flex items-center justify-center gap-2">
            <Plus size={16} aria-hidden="true" />
            Join class
          </span>
        </button>
      </div>

      <div className="overflow-y-auto px-5 py-5 sm:px-6">
        {activeTab === "my-classes" && (
          <>
            {isLoading ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 size={24} className="animate-spin text-orange-300" />
              </div>
            ) : enrolledClasses.length === 0 ? (
              <div className="py-8 text-center">
                <Users size={40} className="mx-auto mb-4 text-white/25" aria-hidden="true" />
                <h3 className="text-base font-bold text-white">No classes yet</h3>
                <p className="mt-2 text-sm text-white/55">
                  Join a class to get personalized learning experiences.
                </p>
                <button
                  type="button"
                  onClick={() => setActiveTab("join")}
                  className={`${cinematicModalPrimaryButtonClass} mt-5`}
                >
                  Join your first class
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <button
                  type="button"
                  onClick={() => handleClassSelect(null)}
                  className={classCardClass(!selectedClassId)}
                >
                  <div className="font-semibold">General learning</div>
                  <div className="text-sm text-white/55">No class selected</div>
                </button>

                {enrolledClasses.map((cls) => (
                  <button
                    key={cls.class_id}
                    type="button"
                    onClick={() => handleClassSelect(cls.class_id)}
                    className={classCardClass(selectedClassId === cls.class_id)}
                  >
                    <div className="font-semibold">{cls.class_name}</div>
                    <div className="text-sm text-white/55">
                      {cls.language_target} • Joined{" "}
                      {new Date(cls.enrolled_at).toLocaleDateString()}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </>
        )}

        {activeTab === "join" && (
          <form onSubmit={handleJoinSubmit} className="space-y-4">
            <div>
              <label htmlFor="classroom-code" className={cinematicModalLabelClass}>
                Class code
              </label>
              <input
                id="classroom-code"
                type="text"
                inputMode="text"
                autoCapitalize="characters"
                value={classCode}
                onChange={(e) => setClassCode(e.target.value.toUpperCase())}
                placeholder="e.g., AB12CD34"
                className={`${cinematicModalInputClass} text-center font-mono text-lg tracking-widest`}
                disabled={isSubmitting}
              />
              <p className="mt-2 text-center text-xs text-white/45">
                Enter the 6-8 character code your teacher shared with you
              </p>
            </div>

            {joinError && (
              <div className="rounded-lg border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-300">
                {joinError}
              </div>
            )}

            {success && (
              <div className="flex items-center gap-2 rounded-lg border border-green-500/20 bg-green-500/10 px-4 py-3 text-sm text-green-300">
                <Check size={16} aria-hidden="true" />
                Joined successfully. Switching to your new class...
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onCloseAction}
                className={`${cinematicModalSecondaryButtonClass} flex-1`}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !classCode.trim()}
                className={`${cinematicModalPrimaryButtonClass} flex-1`}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={18} className="animate-spin" aria-hidden="true" />
                    Joining...
                  </>
                ) : (
                  "Join class"
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </CinematicModal>
  );
}
